import React, { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WalletMultiButton } from '@solana/wallet-adapter-react-ui';
import { Connection, PublicKey, Transaction } from '@solana/web3.js';
import { getAssociatedTokenAddress, createAssociatedTokenAccountInstruction, getAccount, TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID } from '@solana/spl-token';
import {
  fetchAllAuctionsManual,
  fetchActiveAuctionsManual,
  fetchPopperNFTsByOwner,
  placeBidInstruction,
  finalizeAuctionInstruction,
  cancelAuctionInstruction,
  createAuctionInstruction,
  adminForceEndAuctionInstruction,
  formatTimeRemaining,
  getPopperNFTPDA,
  AUCTION_CONSTANTS,
} from '../utils/auctionProgram';
import NFTStatsCard, { readStatsFromReserved } from '../components/NFTStatsCard';
import { fetchNFTMetadata } from '../utils/popperverseProgram';
import { useAdmin } from '../contexts/AdminContext';
import './Auction.css';

// Safely get environment variables with fallbacks
const PROGRAM_ID_STR = process.env.REACT_APP_PROGRAM_ID;
const TREASURY_WALLET_STR = process.env.REACT_APP_TREASURY_WALLET;
const RPC_ENDPOINT = process.env.REACT_APP_RPC_ENDPOINT || 'https://api.devnet.solana.com';

// Only create PublicKey if env var exists
const PROGRAM_ID = PROGRAM_ID_STR ? new PublicKey(PROGRAM_ID_STR) : null;
const TREASURY_WALLET = TREASURY_WALLET_STR ? new PublicKey(TREASURY_WALLET_STR) : null;

export default function Auction() {
  const wallet = useWallet();
  const { isAdmin } = useAdmin();
  const [auctions, setAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [statsAuction, setStatsAuction] = useState(null); // For stats modal
  const [bidAmount, setBidAmount] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // active, my-auctions, ended
  
  // Create Auction Modal State
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userNFTs, setUserNFTs] = useState([]);
  const [loadingNFTs, setLoadingNFTs] = useState(false);
  const [createForm, setCreateForm] = useState({
    nftMint: '',
    startingBid: '1',
    durationDays: '3',
    minBidIncrement: '0.1',
  });

  // Check if environment is configured
  const isConfigured = PROGRAM_ID && TREASURY_WALLET;
  const [error, setError] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  useEffect(() => {
    if (!isConfigured) {
      setLoading(false);
      return;
    }
    loadAuctions(true); // Initial load shows loading state
    const interval = setInterval(() => loadAuctions(false), 30000); // Refresh every 30s, no loading flash
    return () => clearInterval(interval);
  }, [isConfigured]); // Remove wallet.publicKey to prevent re-triggers

  async function loadAuctions(showLoading = false) {
    try {
      setError(null);
      if (showLoading) setLoading(true);
      const connection = new Connection(RPC_ENDPOINT, 'confirmed');
      
      console.log('[Auction] loadAuctions: RPC_ENDPOINT =', RPC_ENDPOINT);
      console.log('[Auction] loadAuctions: PROGRAM_ID =', PROGRAM_ID?.toBase58());

      // Fetch ALL auctions (including ended/broken ones) so sellers can cancel them
      const allAuctions = await fetchAllAuctionsManual(connection, PROGRAM_ID);
      console.log('[Auction] loadAuctions: fetched', allAuctions.length, 'auctions');
      
      // Filter out finalized broken auctions (from old deployment - cannot be cancelled)
      const usableAuctions = allAuctions.filter(auction => {
        const isBroken = auction.account.endTime === 0;
        const isFinalized = auction.account.isFinalized;
        if (isBroken && isFinalized) {
          console.log('[Auction] Filtering out finalized broken auction:', auction.publicKey.toBase58());
          return false;
        }
        return true;
      });
      console.log('[Auction] After filtering:', usableAuctions.length, 'usable auctions');
      
      // Enrich auction data with NFT metadata and stats
      const enrichedAuctions = await Promise.all(
        usableAuctions.map(async (auction, i) => {
          try {
            console.log(`[Auction] Enriching auction ${i}:`, auction.account);
            // Fetch NFT metadata from Metaplex
            const metadata = await fetchNFTMetadata(connection, auction.account.nftMint.toBase58());
            
            // Fetch PopperNFT account to get stats from reserved bytes
            let stats = { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 };
            let totalVotes = 0;
            let totalSales = 0;
            try {
              const [popperNftPDA] = await getPopperNFTPDA(PROGRAM_ID, auction.account.nftMint);
              const popperNftAccount = await connection.getAccountInfo(popperNftPDA);
              if (popperNftAccount) {
                const data = popperNftAccount.data;
                // Read totalVotes at offset 105 (u64)
                if (data.length >= 113) {
                  totalVotes = Number(data.readBigUInt64LE(105));
                }
                // Read totalSales at offset 321 (u64)
                if (data.length >= 329) {
                  totalSales = Number(data.readBigUInt64LE(321));
                }
                // Read reserved bytes starting at offset 355 for stats (128 bytes)
                if (data.length >= 483) {
                  const reserved = data.slice(355, 483);
                  const parsedStats = readStatsFromReserved(reserved);
                  if (parsedStats && parsedStats.averages) {
                    stats = parsedStats.averages;
                  }
                }
              }
            } catch (statsError) {
              console.warn(`[Auction] Could not fetch stats for auction ${i}:`, statsError);
            }
            
            const enriched = {
              ...auction,
              metadata: metadata,
              name: metadata?.name || `Popper #${auction.account.nftMint.toBase58().slice(0, 8)}`,
              image: metadata?.image || '/Design/silhouette.png',
              stats,
              totalVotes,
              totalSales,
            };
            console.log(`[Auction] Enriched auction ${i}:`, enriched);
            return enriched;
          } catch (error) {
            console.error('[Auction] Failed to fetch metadata for', auction.account.nftMint.toBase58(), error);
            return {
              ...auction,
              name: `Popper #${auction.account.nftMint.toBase58().slice(0, 8)}`,
              image: '/Design/silhouette.png',
              stats: { cuteness: 0, creativity: 0, popPower: 0, originality: 0, potential: 0 },
              totalVotes: 0,
              totalSales: 0,
            };
          }
        })
      );

      console.log('[Auction] Setting', enrichedAuctions.length, 'enriched auctions to state');
      setAuctions(enrichedAuctions);
    } catch (error) {
      console.error('Error loading auctions:', error);
      setError(error.message || 'Failed to load auctions');
    } finally {
      setLoading(false);
    }
  }

  async function handlePlaceBid(auction) {
    if (!wallet.publicKey) {
      alert('Please connect your wallet');
      return;
    }

    if (!bidAmount || parseFloat(bidAmount) <= 0) {
      alert('Please enter a valid bid amount');
      return;
    }

    const bidLamports = parseFloat(bidAmount) * 1e9;
    // Manual parsing returns plain numbers, not BN objects
    const currentBid = auction.account.highestBid;
    const minBid = currentBid === 0 
      ? auction.account.startingBid
      : currentBid + auction.account.minBidIncrement;

    if (bidLamports < minBid) {
      alert(`Minimum bid is ${(minBid / 1e9).toFixed(2)} SOL`);
      return;
    }

    try {
      const connection = new Connection(RPC_ENDPOINT);

      const previousBidder = auction.account.highestBidder || wallet.publicKey;
      
      console.log('[Auction] Place bid params:', {
        bidder: wallet.publicKey.toString(),
        auctionState: auction.publicKey.toString(),
        previousBidder: previousBidder.toString(),
        bidAmount: parseFloat(bidAmount),
        highestBidderExists: !!auction.account.highestBidder,
      });

      // Build transaction using raw instruction (no Anchor dependency)
      const tx = await placeBidInstruction({
        programId: PROGRAM_ID,
        bidder: wallet.publicKey,
        auctionState: auction.publicKey,
        previousBidder,
        bidAmount: parseFloat(bidAmount),
      });

      // Set recent blockhash before simulation
      tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
      tx.feePayer = wallet.publicKey;
      
      // Simulate first to get detailed error
      console.log('[Auction] Simulating transaction...');
      const simulation = await connection.simulateTransaction(tx);
      console.log('[Auction] Simulation result:', simulation.value);
      
      if (simulation.value.err) {
        console.error('[Auction] Simulation error:', simulation.value.err);
        console.error('[Auction] Simulation logs:', simulation.value.logs);
        throw new Error('Transaction simulation failed: ' + JSON.stringify(simulation.value.err) + '\nLogs: ' + (simulation.value.logs || []).join('\n'));
      }

      const signature = await wallet.sendTransaction(tx, connection);
      await connection.confirmTransaction(signature, 'confirmed');

      alert('Bid placed successfully!');
      setBidAmount('');
      setSelectedAuction(null);
      loadAuctions();
    } catch (error) {
      console.error('Error placing bid:', error);
      alert('Failed to place bid: ' + error.message);
    }
  }

  async function handleFinalize(auction) {
    if (!wallet.publicKey) {
      alert('Please connect your wallet');
      return;
    }

    try {
      const connection = new Connection(RPC_ENDPOINT, 'confirmed');

      // Data is already parsed as plain JS objects (Pubkey instances)
      const winner = auction.account.highestBidder || auction.account.seller;
      const sellerNftAccount = await getAssociatedTokenAddress(
        auction.account.nftMint,
        auction.account.seller
      );
      const winnerNftAccount = await getAssociatedTokenAddress(
        auction.account.nftMint,
        winner
      );

      console.log('[Auction] Finalize params:', {
        programId: PROGRAM_ID?.toBase58(),
        finalizer: wallet.publicKey?.toBase58(),
        auctionState: auction.publicKey?.toBase58(),
        nftMint: auction.account.nftMint?.toBase58(),
        seller: auction.account.seller?.toBase58(),
        sellerNftAccount: sellerNftAccount?.toBase58(),
        winner: winner?.toBase58(),
        winnerNftAccount: winnerNftAccount?.toBase58(),
        treasury: TREASURY_WALLET?.toBase58(),
        highestBid: auction.account.highestBid,
        hasHighestBidder: !!auction.account.highestBidder,
      });

      // Build the transaction
      const tx = new Transaction();

      // Check if winner's token account exists, create it if not
      try {
        await getAccount(connection, winnerNftAccount);
        console.log('[Auction] Winner NFT account exists');
      } catch (e) {
        // Account doesn't exist, need to create it
        console.log('[Auction] Creating winner NFT account...');
        const createAtaIx = createAssociatedTokenAccountInstruction(
          wallet.publicKey,      // payer
          winnerNftAccount,      // associatedToken
          winner,                // owner
          auction.account.nftMint, // mint
          TOKEN_PROGRAM_ID,
          ASSOCIATED_TOKEN_PROGRAM_ID
        );
        tx.add(createAtaIx);
      }

      // Build finalize instruction using raw instruction (no Anchor dependency)
      const finalizeTx = await finalizeAuctionInstruction({
        programId: PROGRAM_ID,
        finalizer: wallet.publicKey,
        auctionState: auction.publicKey,
        nftMint: auction.account.nftMint,
        seller: auction.account.seller,
        sellerNftAccount,
        winner,
        winnerNftAccount,
        treasury: TREASURY_WALLET,
      });

      // Add finalize instruction(s) to our transaction
      finalizeTx.instructions.forEach(ix => tx.add(ix));

      // Set blockhash and fee payer before simulation
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
      tx.recentBlockhash = blockhash;
      tx.feePayer = wallet.publicKey;

      // Simulate first to get detailed error
      console.log('[Auction] Simulating finalize transaction...');
      const simulation = await connection.simulateTransaction(tx);
      console.log('[Auction] Simulation result:', simulation.value);
      
      if (simulation.value.err) {
        console.error('[Auction] Simulation error:', simulation.value.err);
        console.error('[Auction] Simulation logs:', simulation.value.logs);
        throw new Error('Transaction simulation failed: ' + JSON.stringify(simulation.value.err) + '\nLogs: ' + (simulation.value.logs || []).join('\n'));
      }

      const signature = await wallet.sendTransaction(tx, connection);
      await connection.confirmTransaction({ signature, blockhash, lastValidBlockHeight }, 'confirmed');

      alert('Auction finalized successfully!');
      loadAuctions();
    } catch (error) {
      console.error('Error finalizing auction:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        logs: error.logs,
      });
      alert('Failed to finalize auction: ' + error.message);
    }
  }

  // Admin-only: Force end an auction by setting end_time to now
  async function handleForceEndAuction(auction) {
    if (!wallet.publicKey || !isAdmin) {
      alert('Admin access required');
      return;
    }

    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to force-end this auction? This will set the end time to now, allowing immediate finalization.')) {
      return;
    }

    try {
      const connection = new Connection(RPC_ENDPOINT);

      console.log('[Auction] Admin force-ending auction:', auction.publicKey.toString());

      // Build transaction using raw instruction
      const tx = await adminForceEndAuctionInstruction({
        programId: PROGRAM_ID,
        authority: wallet.publicKey,
        auctionState: auction.publicKey,
      });

      // Set recent blockhash before sending
      tx.recentBlockhash = (await connection.getLatestBlockhash()).blockhash;
      tx.feePayer = wallet.publicKey;

      const signature = await wallet.sendTransaction(tx, connection);
      await connection.confirmTransaction(signature, 'confirmed');

      alert('Auction force-ended! You can now finalize it.');
      loadAuctions();
    } catch (error) {
      console.error('Error force-ending auction:', error);
      alert('Failed to force-end auction: ' + error.message);
    }
  }

  async function loadUserNFTs() {
    if (!wallet.publicKey) return;

    setLoadingNFTs(true);
    try {
      const connection = new Connection(RPC_ENDPOINT);

      console.log('[Auction] Loading NFTs for wallet:', wallet.publicKey.toBase58());
      
      // Use manual parsing to fetch user's NFTs
      const nfts = await fetchPopperNFTsByOwner(connection, PROGRAM_ID, wallet.publicKey);
      
      console.log('[Auction] Found', nfts.length, 'NFTs owned by wallet');
      nfts.forEach((nft, i) => {
        console.log(`[Auction] NFT ${i}: mint=${nft.account.nftMint?.toString()}, owner=${nft.account.currentOwner?.toString()}`);
      });

      // Filter out NFTs that are already in active auctions
      const activeAuctionNFTs = new Set(
        auctions
          .filter(a => !a.account.isFinalized && !a.account.isCancelled)
          .map(a => a.account.nftMint.toString())
      );
      
      console.log('[Auction] Active auction NFTs:', [...activeAuctionNFTs]);

      const availableNFTs = nfts.filter(
        nft => !activeAuctionNFTs.has(nft.account.nftMint.toString())
      );
      
      console.log('[Auction] Available NFTs for auction:', availableNFTs.length);

      // Fetch metadata for each NFT to get names and images
      const enrichedNFTs = await Promise.all(
        availableNFTs.map(async (nft) => {
          try {
            const metadata = await fetchNFTMetadata(connection, nft.account.nftMint.toBase58());
            return {
              ...nft,
              metadata: metadata,
              name: metadata?.name || `Popper #${nft.account.nftMint.toBase58().slice(0, 8)}`,
              image: metadata?.image || metadata?.metadata_uri || '/Design/silhouette.png',
            };
          } catch (error) {
            console.error('[Auction] Failed to fetch metadata for', nft.account.nftMint.toBase58(), error);
            return {
              ...nft,
              name: `Popper #${nft.account.nftMint.toBase58().slice(0, 8)}`,
              image: '/Design/silhouette.png',
            };
          }
        })
      );

      setUserNFTs(enrichedNFTs);
    } catch (error) {
      console.error('Error loading user NFTs:', error);
    } finally {
      setLoadingNFTs(false);
    }
  }

  async function handleCreateAuction() {
    if (!wallet.publicKey) {
      alert('Please connect your wallet');
      return;
    }

    if (!createForm.nftMint) {
      alert('Please select an NFT');
      return;
    }

    const startingBid = parseFloat(createForm.startingBid);
    const durationDays = parseFloat(createForm.durationDays);
    const minBidIncrement = parseFloat(createForm.minBidIncrement);

    if (startingBid <= 0) {
      alert('Starting bid must be greater than 0');
      return;
    }

    if (durationDays < 3 || durationDays > 30) {
      alert('Duration must be between 3 and 30 days');
      return;
    }

    if (minBidIncrement < 0.1) {
      alert('Minimum bid increment must be at least 0.1 SOL');
      return;
    }

    try {
      const connection = new Connection(RPC_ENDPOINT);

      const nftMint = new PublicKey(createForm.nftMint);
      const nftTokenAccount = await getAssociatedTokenAddress(nftMint, wallet.publicKey);

      // Build transaction using raw instruction (no Anchor dependency)
      const tx = await createAuctionInstruction({
        programId: PROGRAM_ID,
        seller: wallet.publicKey,
        nftMint,
        nftTokenAccount,
        startingBid,
        durationDays,
        minBidIncrement,
      });

      const signature = await wallet.sendTransaction(tx, connection);
      await connection.confirmTransaction(signature, 'confirmed');

      alert('Auction created successfully!');
      setShowCreateModal(false);
      setCreateForm({
        nftMint: '',
        startingBid: '1',
        durationDays: '3',
        minBidIncrement: '0.1',
      });
      loadAuctions();
    } catch (error) {
      console.error('Error creating auction:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        logs: error.logs,
        stack: error.stack
      });
      alert('Failed to create auction: ' + (error.message || error.toString()));
    }
  }

  async function handleCancelAuction(auction) {
    if (!wallet.publicKey) return;
    
    if (!window.confirm('Are you sure you want to cancel this auction? The NFT will be returned to you.')) {
      return;
    }

    try {
      const connection = new Connection(RPC_ENDPOINT, 'confirmed');
      
      // Get seller's token account for the NFT
      const sellerNftAccount = await getAssociatedTokenAddress(
        auction.account.nftMint,
        wallet.publicKey
      );
      
      console.log('[Auction] Cancel params:', {
        programId: PROGRAM_ID?.toBase58(),
        seller: wallet.publicKey?.toBase58(),
        nftMint: auction.account.nftMint?.toBase58(),
        auctionState: auction.publicKey?.toBase58(),
        sellerNftAccount: sellerNftAccount?.toBase58(),
      });
      
      // Create instruction
      const instruction = cancelAuctionInstruction({
        programId: PROGRAM_ID,
        seller: wallet.publicKey,
        nftMint: auction.account.nftMint,
        auctionState: auction.publicKey,
        sellerNftAccount,
      });
      
      // Create transaction with proper setup
      const { blockhash, lastValidBlockHeight } = await connection.getLatestBlockhash();
      const tx = new Transaction();
      tx.recentBlockhash = blockhash;
      tx.feePayer = wallet.publicKey;
      tx.add(instruction);
      
      console.log('[Auction] Cancel transaction created:', {
        feePayer: tx.feePayer?.toBase58(),
        blockhash: tx.recentBlockhash,
        lastValidBlockHeight,
        instructions: tx.instructions.length,
        instructionKeys: tx.instructions[0].keys.map(k => ({ 
          pubkey: k.pubkey.toBase58(), 
          isSigner: k.isSigner, 
          isWritable: k.isWritable 
        })),
        instructionProgramId: tx.instructions[0].programId.toBase58(),
        instructionDataLength: tx.instructions[0].data.length,
      });
      
      // Try to serialize to catch any issues
      try {
        const serialized = tx.serialize({ requireAllSignatures: false });
        console.log('[Auction] Transaction serialized successfully, length:', serialized.length);
      } catch (serError) {
        console.error('[Auction] Transaction serialization failed:', serError);
      }
      
      // Try using signTransaction instead for better error messages
      console.log('[Auction] Attempting to sign transaction...');
      let signedTx;
      try {
        signedTx = await wallet.signTransaction(tx);
        console.log('[Auction] Transaction signed successfully');
      } catch (signError) {
        console.error('[Auction] Sign error:', signError);
        throw signError;
      }
      
      // Send the signed transaction
      console.log('[Auction] Sending signed transaction...');
      const rawTransaction = signedTx.serialize();
      const signature = await connection.sendRawTransaction(rawTransaction, {
        skipPreflight: false,
        preflightCommitment: 'confirmed',
      });
      console.log('[Auction] Transaction sent:', signature);
      
      await connection.confirmTransaction(signature, 'confirmed');

      alert('Auction cancelled successfully!');
      loadAuctions();
    } catch (error) {
      console.error('Error cancelling auction:', error);
      console.error('Error name:', error.name);
      console.error('Error message:', error.message);
      console.error('Error cause:', error.cause);
      console.error('Error logs:', error.logs);
      alert('Failed to cancel auction: ' + (error.message || error.toString()));
    }
  }

  function openCreateModal() {
    if (!wallet.publicKey) {
      alert('Please connect your wallet first');
      return;
    }
    setShowCreateModal(true);
    loadUserNFTs();
  }

  const filterAuctions = () => {
    const now = Math.floor(Date.now() / 1000);
    console.log('[Auction] filterAuctions: now =', now, 'total auctions =', auctions.length);
    
    switch (activeTab) {
      case 'active':
        const active = auctions.filter(a => {
          const isActive = a.account.endTime > now && !a.account.isFinalized;
          console.log('[Auction] Filter active:', a.account.nftMint?.toBase58(), 'endTime:', a.account.endTime, 'now:', now, 'isFinalized:', a.account.isFinalized, 'result:', isActive);
          return isActive;
        });
        console.log('[Auction] Active auctions:', active.length);
        return active;
      case 'my-auctions':
        return auctions.filter(a => 
          (wallet.publicKey && a.account.seller.equals(wallet.publicKey)) ||
          (wallet.publicKey && a.account.highestBidder?.equals(wallet.publicKey))
        );
      case 'ended':
        return auctions.filter(a => a.account.endTime <= now || a.account.isFinalized);
      default:
        return auctions;
    }
  };

  const filteredAuctions = filterAuctions();

  // Show configuration error if environment not set up (check FIRST before loading)
  if (!isConfigured) {
    return (
      <div className="auction-container">
        <header className="auction-header">
          <h1>POPPER AUCTION HOUSE</h1>
          <WalletMultiButton />
        </header>
        <div className="auction-error" style={{ padding: '40px', textAlign: 'center' }}>
          <h2>⚠️ Configuration Error</h2>
          <p>Program ID or Treasury Wallet not configured.</p>
          <p style={{ fontSize: '12px', color: '#888' }}>
            REACT_APP_PROGRAM_ID: {PROGRAM_ID_STR || 'undefined'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="auction-container">
        <header className="auction-header">
          <h1>POPPER AUCTION HOUSE</h1>
          <WalletMultiButton />
        </header>
        <div className="loading" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading auctions...</p>
          {error && <p style={{ color: '#ff6b6b', marginTop: '10px' }}>Error: {error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="auction-container">
      <header className="auction-header">
        <div>
          <h1>POPPER AUCTION HOUSE</h1>
          <button className="create-auction-btn" onClick={openCreateModal}>
            + Start Auction
          </button>
        </div>
        <WalletMultiButton />
      </header>

      <div className="auction-tabs">
        <button 
          className={activeTab === 'active' ? 'active' : ''}
          onClick={() => setActiveTab('active')}
        >
          Active Auctions ({auctions.filter(a => a.account.endTime > Math.floor(Date.now() / 1000)).length})
        </button>
        <button 
          className={activeTab === 'my-auctions' ? 'active' : ''}
          onClick={() => setActiveTab('my-auctions')}
          disabled={!wallet.publicKey}
        >
          My Auctions
        </button>
        <button 
          className={activeTab === 'ended' ? 'active' : ''}
          onClick={() => setActiveTab('ended')}
        >
          Ended
        </button>
      </div>

      {filteredAuctions.length === 0 ? (
        <div className="no-auctions">
          <p>No auctions found in this category</p>
          {activeTab === 'active' && <p>Check back soon for new listings!</p>}
        </div>
      ) : (
        <div className="auction-grid">
          {filteredAuctions.map((auction) => {
            const now = Math.floor(Date.now() / 1000);
            const isBroken = auction.account.endTime === 0;
            const isEnded = auction.account.endTime <= now && !isBroken;
            const currentBid = auction.account.highestBid / 1e9;
            const startingBid = auction.account.startingBid / 1e9;
            const minNextBid = currentBid > 0 
              ? currentBid + (auction.account.minBidIncrement / 1e9)
              : startingBid;

            return (
              <div key={auction.publicKey.toString()} className={`auction-card ${isBroken ? 'broken' : ''} ${isEnded ? 'ended' : ''}`}>
                {isBroken && <div className="broken-badge">⚠️ BROKEN - Cancel to Reclaim NFT</div>}
                
                {/* NFT Image Frame - Prominent at top */}
                <div className="auction-image-frame">
                  <img 
                    src={auction.metadata?.metadata_uri || '/Design/silhouette.png'} 
                    alt={auction.metadata?.name || 'Popper NFT'}
                    onError={(e) => { e.target.src = '/Design/silhouette.png'; }}
                  />
                </div>
                
                {/* NFT Name */}
                <h4 className="auction-name">{auction.metadata?.name || 'Popper NFT'}</h4>
                
                {/* Current Bid / Price */}
                <div className="auction-price">
                  <span className="price-value">
                    {currentBid > 0 ? `${currentBid.toFixed(2)} SOL` : `${startingBid.toFixed(2)} SOL`}
                  </span>
                  <span className="price-label">
                    {currentBid > 0 ? 'Current Bid' : 'Starting Bid'}
                  </span>
                </div>
                
                {/* Auction Info Pills */}
                <div className="auction-info-pills">
                  <div className="info-pill">
                    <span className="pill-icon">🔨</span>
                    <span className="pill-value">{auction.account.bidCount}</span>
                    <span className="pill-label">Bids</span>
                  </div>
                  <div className={`info-pill ${isEnded ? 'ended' : ''}`}>
                    <span className="pill-icon">⏰</span>
                    <span className="pill-value">{formatTimeRemaining(auction.account.endTime)}</span>
                    <span className="pill-label">Remaining</span>
                  </div>
                </div>
                
                {/* Leading Bidder */}
                {auction.account.highestBidder && (
                  <div className="leading-bidder">
                    <span className="bidder-label">👑 Leading:</span>
                    <span className="bidder-address">
                      {auction.account.highestBidder.toString().slice(0, 4)}...
                      {auction.account.highestBidder.toString().slice(-4)}
                    </span>
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="auction-buttons">
                  {!isEnded && !auction.account.isFinalized && (
                    <>
                      {wallet.publicKey ? (
                        wallet.publicKey.equals(auction.account.seller) ? (
                          <button 
                            className="cancel-btn"
                            onClick={() => handleCancelAuction(auction)}
                          >
                            Cancel Auction
                          </button>
                        ) : (
                          <div className="bid-controls">
                            <input
                              type="number"
                              placeholder={`Min: ${minNextBid.toFixed(2)}`}
                              value={selectedAuction?.publicKey.equals(auction.publicKey) ? bidAmount : ''}
                              onChange={(e) => {
                                setSelectedAuction(auction);
                                setBidAmount(e.target.value);
                              }}
                              onFocus={() => {
                                if (!selectedAuction?.publicKey.equals(auction.publicKey)) {
                                  setSelectedAuction(auction);
                                  setBidAmount(minNextBid.toFixed(2));
                                }
                              }}
                              step="0.1"
                              min={minNextBid}
                            />
                            <button 
                              className="bid-btn"
                              onClick={() => handlePlaceBid(auction)}
                              disabled={!selectedAuction?.publicKey.equals(auction.publicKey) || !bidAmount || parseFloat(bidAmount) < minNextBid}
                            >
                              Place Bid
                            </button>
                          </div>
                        )
                      ) : (
                        <button className="connect-btn" disabled>
                          Connect Wallet to Bid
                        </button>
                      )}
                    </>
                  )}

                  {isEnded && !auction.account.isFinalized && (
                    <button 
                      className="finalize-btn"
                      onClick={() => handleFinalize(auction)}
                    >
                      🏆 Finalize Auction
                    </button>
                  )}

                  {/* Admin-only: Force end active auctions for testing */}
                  {isAdmin && !isEnded && !auction.account.isFinalized && (
                    <button 
                      className="force-end-btn admin-btn"
                      onClick={() => handleForceEndAuction(auction)}
                    >
                      ⚡ Force End (Admin)
                    </button>
                  )}

                  {auction.account.isFinalized && (
                    <div className="finalized-badge">✓ Finalized</div>
                  )}

                  <button 
                    className="stats-btn"
                    onClick={() => setStatsAuction(auction)}
                  >
                    📊 View Stats
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="auction-info-box">
        <h3>ℹ️ How It Works</h3>
        <ul>
          <li><strong>Minimum Duration:</strong> 3 days</li>
          <li><strong>Platform Fee:</strong> 2.5% on winning bid</li>
          <li><strong>Anti-Snipe:</strong> Bids in last 5 minutes extend auction by 5 minutes</li>
          <li><strong>Min Increment:</strong> 0.1 SOL between bids</li>
          <li><strong>Finalization:</strong> When an auction ends, click "Finalize" to complete the sale</li>
        </ul>
      </div>

      {/* Create Auction Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Start New Auction</h2>
              <button className="close-btn" onClick={() => setShowCreateModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {loadingNFTs ? (
                <div className="loading">Loading your NFTs...</div>
              ) : userNFTs.length === 0 ? (
                <div className="no-nfts">
                  <p>You don't have any NFTs available for auction.</p>
                  <p>Create an NFT first or wait for existing auctions to end.</p>
                </div>
              ) : (
                <form className="create-auction-form">
                  <div className="form-group">
                    <label>Select NFT:</label>
                    <select
                      value={createForm.nftMint}
                      onChange={(e) => setCreateForm({ ...createForm, nftMint: e.target.value })}
                    >
                      <option value="">-- Choose an NFT --</option>
                      {userNFTs.map((nft) => (
                        <option key={nft.account.nftMint.toString()} value={nft.account.nftMint.toString()}>
                          {nft.name} ({nft.account.nftMint.toString().slice(0, 4)}...{nft.account.nftMint.toString().slice(-4)})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Starting Bid (SOL):</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={createForm.startingBid}
                      onChange={(e) => setCreateForm({ ...createForm, startingBid: e.target.value })}
                    />
                  </div>

                  <div className="form-group">
                    <label>Duration (days):</label>
                    <input
                      type="number"
                      min="3"
                      max="30"
                      step="1"
                      value={createForm.durationDays}
                      onChange={(e) => setCreateForm({ ...createForm, durationDays: e.target.value })}
                    />
                    <small>Minimum: 3 days | Maximum: 30 days</small>
                  </div>

                  <div className="form-group">
                    <label>Minimum Bid Increment (SOL):</label>
                    <input
                      type="number"
                      min="0.1"
                      step="0.1"
                      value={createForm.minBidIncrement}
                      onChange={(e) => setCreateForm({ ...createForm, minBidIncrement: e.target.value })}
                    />
                    <small>Minimum: 0.1 SOL</small>
                  </div>

                  {/* NFT Preview */}
                  {createForm.nftMint && (() => {
                    const selectedNft = userNFTs.find(nft => nft.account.nftMint.toString() === createForm.nftMint);
                    if (!selectedNft) return null;
                    return (
                      <div className="nft-preview">
                        <label>Selected NFT:</label>
                        <div className="nft-preview-card">
                          <img 
                            src={selectedNft.image} 
                            alt={selectedNft.name}
                            onError={(e) => { e.target.src = '/Design/silhouette.png'; }}
                          />
                          <div className="nft-preview-info">
                            <span className="nft-preview-name">{selectedNft.name}</span>
                            <span className="nft-preview-mint">
                              {createForm.nftMint.slice(0, 6)}...{createForm.nftMint.slice(-6)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="form-actions">
                    <button
                      type="button"
                      className="cancel-btn"
                      onClick={() => setShowCreateModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="submit-btn"
                      onClick={handleCreateAuction}
                      disabled={!createForm.nftMint}
                    >
                      Create Auction
                    </button>
                  </div>

                  <div className="fee-notice">
                    <strong>⚠️ Note:</strong> A 2.5% platform fee will be deducted from the final sale price.
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* NFT Stats Modal */}
      {statsAuction && (
        <div className="modal-overlay stats-modal-overlay" onClick={() => setStatsAuction(null)}>
          <NFTStatsCard
            name={statsAuction.name || statsAuction.metadata?.name || 'Popper NFT'}
            description={statsAuction.metadata?.description || 'A unique Popper NFT'}
            imageUrl={statsAuction.image || statsAuction.metadata?.image || '/Design/silhouette.png'}
            stats={statsAuction.stats || {}}
            totalVotes={statsAuction.totalVotes || 0}
            totalSales={statsAuction.totalSales || 0}
            onClose={() => setStatsAuction(null)}
          />
        </div>
      )}
    </div>
  );
}
